import React, { createContext, useContext, useState, useEffect } from "react";
import {
  getProfile,
  register as apiRegister,
  login as apiLogin,
  logout as apiLogout,
  logoutAllSessions as apiLogoutAllSessions,
  googleOAuth as apiGoogleOAuth,
} from "../services/api";
import PropTypes from "prop-types";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUserProfile = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const userData = await getProfile();
        console.log("🔍 [AUTH] Received user data from API:", userData);
        console.log("🔍 [AUTH] User is_admin value:", userData.is_admin);
        console.log("🔍 [AUTH] User is_admin type:", typeof userData.is_admin);
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        console.error("🔍 [AUTH] Error fetching user profile:", error);
        // Clear everything on error
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        setUser(null);
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const login = async (email, password) => {
    try {
      // Clear any existing state before login
      setUser(null);
      setIsAuthenticated(false);

      // Clear auth-specific cached data before login (preserve theme, sidebar, etc.)
      ["token", "refreshToken", "trading_symbols_cache", "exchanges_cache", "strategies_cache"].forEach((key) => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });

      console.log("🔍 [AUTH] Starting fresh login for:", email);

      const response = await apiLogin(email, password);
      localStorage.setItem("token", response.access_token);
      localStorage.setItem("refreshToken", response.refresh_token);

      console.log("🔍 [AUTH] Login successful, fetching profile...");
      await fetchUserProfile();
    } catch (error) {
      // Clear auth state on login error
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem("token");
      localStorage.removeItem("refreshToken");
      throw error;
    }
  };

  const clearAuthState = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    // Clear data caches but preserve UI preferences (theme, sidebar)
    ["trading_symbols_cache", "exchanges_cache", "strategies_cache"].forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    setUser(null);
    setIsAuthenticated(false);
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error("[AuthContext] Logout error:", error);
    } finally {
      clearAuthState();
    }
  };

  const logoutAllSessions = async () => {
    try {
      await apiLogoutAllSessions();
    } catch (error) {
      console.error("[AuthContext] Logout all sessions error:", error);
    } finally {
      clearAuthState();
    }
  };

  const register = async (email, username, password) => {
    return await apiRegister({ email, username, password, full_name: username });
  };

  const googleOAuth = async (code) => {
    try {
      const response = await apiGoogleOAuth(code);
      localStorage.setItem("token", response.access_token);
      localStorage.setItem("refreshToken", response.refresh_token);
      await fetchUserProfile();
      return response;
    } catch (error) {
      throw error;
    }
  };

  const forceResetAuth = () => {
    console.log("🔍 [AUTH] Force reset authentication state");

    // Clear React state
    setUser(null);
    setIsAuthenticated(false);

    // Clear all storage
    localStorage.clear();
    sessionStorage.clear();

    // Clear specific cache keys
    const cacheKeys = [
      "token",
      "refreshToken",
      "user",
      "auth",
      "trading_symbols_cache",
      "exchanges_cache",
      "strategies_cache",
    ];

    cacheKeys.forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    // Clear browser cache if possible
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    // Clear any service worker cache
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister();
        });
      });
    }

    console.log("🔍 [AUTH] All caches cleared, redirecting to login");

    // Force redirect to login with cache-busting
    window.location.href = "/login?t=" + Date.now();
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        login,
        logout,
        logoutAllSessions,
        register,
        googleOAuth,
        loading,
        forceResetAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
